import { useState, useMemo } from "react";
import "./Offer.css";
import { packages, themes, durations, priceRanges, packageTypes, sortOptions, filterAndSort, formatPrice } from "./offerdata.js";
import { Plane } from "lucide-react";
import usePageMeta from "../../../hooks/usePageMeta";

// ── Icons (inline SVG helpers) ────────────────────────────────────────────────
const Icon = {
  Search: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
    </svg>
  ),
  Grid: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
    </svg>
  ),
  List: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
      <line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/>
      <line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
    </svg>
  ),
  Heart: ({ filled }) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill={filled ? "#e05e5e" : "none"} stroke={filled ? "#e05e5e" : "#666"} strokeWidth="2">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  ),
  Arrow: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
  ),
  Filter: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
    </svg>
  ),
  Close: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Chat: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  Check: () => (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  Plane: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
    </svg>
  ),
};

// Amenity icons map
const AMENITY_ICONS = {
  hotel: { icon: "<Hotel size={18} />", label: "Hotel" },
  transport: { icon: "🚌", label: "Transfer" },
  guide: { icon: "🎧", label: "Guide" },
  meals: { icon: "🍽️", label: "Meals" },
};

// ── Package Card ──────────────────────────────────────────────────────────────
function PackageCard({ pkg, isCompared, onToggleCompare, isListView }) {
  const [wished, setWished] = useState(false);
  const discount = pkg.originalPrice
    ? Math.round(((pkg.originalPrice - pkg.discounted_price) / pkg.originalPrice) * 100)
    : null;

  return (
    <div className={`package-card${isListView ? "" : ""}`}>
      {/* Image */}
      <div className="card-image-wrap">
        <img src={pkg.image} alt={pkg.title} className="card-image" loading="lazy" />
        <div className="card-image-overlay" />

        {/* Top row badges */}
        <div className="card-top-row">
          <span className="duration-badge">{pkg.duration} · {pkg.type}</span>
          {pkg.badge && <span className="exclusive-badge">✦ {pkg.badge}</span>}
        </div>

        {/* Compare checkbox */}
        <div className="compare-check">
          <label className={`compare-checkbox${isCompared ? " checked" : ""}`}>
            <input
              type="checkbox"
              checked={isCompared}
              onChange={() => onToggleCompare(pkg)}
            />
            <span className="checkbox-box">
              <span className="checkbox-check"><Icon.Check /></span>
            </span>
            <span>Compare</span>
          </label>
        </div>

        {/* Airline */}
        {pkg.airline && (
          <div className="airline-badge">
            <Icon.Plane /> {pkg.airline}
          </div>
        )}

        {/* Wishlist */}
        <button
          className={`wishlist-btn${wished ? " saved" : ""}`}
          onClick={(e) => { e.stopPropagation(); setWished(!wished); }}
          title={wished ? "Remove from wishlist" : "Add to wishlist"}
        >
          <Icon.Heart filled={wished} />
        </button>
      </div>

      {/* Body */}
      <div className="card-body">
        <div className="card-destinations">
          {pkg.destinations.map((d, i) => (
            <span key={i}>
              {i > 0 && <span className="dest-dot">·</span>}
              {d}
            </span>
          ))}
        </div>

        <h3 className="card-title">{pkg.title}</h3>
        <p className="card-subtitle">{pkg.subtitle}</p>

        <div className="theme-tags">
          {pkg.theme.map((t) => (
            <span key={t} className="theme-tag">{t}</span>
          ))}
        </div>

        <div className="card-amenities">
          {pkg.amenities.map((a) => (
            <span key={a} className="amenity" title={AMENITY_ICONS[a]?.label}>
              <span className="amenity-icon">{AMENITY_ICONS[a]?.icon}</span>
              <span>{AMENITY_ICONS[a]?.label}</span>
            </span>
          ))}
        </div>

        <ul className="card-highlights">
          {pkg.highlights.slice(0, isListView ? 2 : 3).map((h, i) => (
            <li key={i} className="highlight-item">
              <span className="highlight-dot" />
              {h}
            </li>
          ))}
        </ul>

        <div className="card-footer">
          <div className="card-price-wrap">
            {discount && <span className="discount-tag">Save {discount}%</span>}
            {pkg.originalPrice && (
              <div className="original-price">₹{formatPrice(pkg.originalPrice)}</div>
            )}
            <div className="price-main">
              <span className="price-currency">₹</span>
              <span className="price-amount">{formatPrice(pkg.discounted_price)}</span>
            </div>
            <div className="price-per">Per person on twin sharing</div>
          </div>

          <button className="book-btn">
            Book Now <span className="arrow"><Icon.Arrow /></span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sidebar Filters ───────────────────────────────────────────────────────────
function Sidebar({ filters, setFilters, onReset }) {
  const setFilter = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <span className="sidebar-title">
          <Icon.Filter /> &nbsp;Filters
        </span>
        <button className="reset-btn" onClick={onReset}>Reset All</button>
      </div>

      <div className="filter-section">
        <span className="filter-label">Themes</span>
        <div className="filter-chips">
          {themes.map((t) => (
            <button
              key={t}
              className={`chip${filters.theme === t ? " active" : ""}`}
              onClick={() => setFilter("theme", t)}
            >{t}</button>
          ))}
        </div>
      </div>

      <div className="filter-section">
        <span className="filter-label">Duration</span>
        <div className="filter-chips">
          {durations.map((d) => (
            <button
              key={d}
              className={`chip${filters.duration === d ? " active" : ""}`}
              onClick={() => setFilter("duration", d)}
            >{d}</button>
          ))}
        </div>
      </div>

      <div className="filter-section">
        <span className="filter-label">Price Range</span>
        <select
          className="filter-select"
          value={filters.price}
          onChange={(e) => setFilter("price", e.target.value)}
        >
          {priceRanges.map((p) => <option key={p}>{p}</option>)}
        </select>
      </div>

      <div className="filter-section">
        <span className="filter-label">Package Type</span>
        <div className="filter-chips">
          {packageTypes.map((p) => (
            <button
              key={p}
              className={`chip${filters.packageType === p ? " active" : ""}`}
              onClick={() => setFilter("packageType", p)}
            >{p}</button>
          ))}
        </div>
      </div>

      <div className="filter-section">
        <span className="filter-label">Flight Included</span>
        <div className="filter-chips">
          <button
            className={`chip${filters.flight === "all" ? " active" : ""}`}
            onClick={() => setFilter("flight", "all")}
          >All</button>
          <button
            className={`chip${filters.flight === "yes" ? " active" : ""}`}
            onClick={() => setFilter("flight", "yes")}
          ><Plane size={18} /> Included</button>
          <button
            className={`chip${filters.flight === "no" ? " active" : ""}`}
            onClick={() => setFilter("flight", "no")}
          >Land Only</button>
        </div>
      </div>
    </aside>
  );
}

// ── Compare Modal ─────────────────────────────────────────────────────────────
function CompareModal({ items, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Compare Packages</h2>
          <button className="modal-close" onClick={onClose}><Icon.Close /></button>
        </div>
        <div className="compare-grid">
          {items.map((pkg) => {
            const discount = pkg.originalPrice
              ? Math.round(((pkg.originalPrice - pkg.discounted_price) / pkg.originalPrice) * 100)
              : null;
            return (
              <div key={pkg.id} className="compare-card">
                <img src={pkg.image} alt={pkg.title} className="compare-card-img" />
                <div className="compare-card-info">
                  <h3 className="compare-card-title">{pkg.title}</h3>
                  <div className="compare-card-row">
                    <span>Duration</span>
                    <strong>{pkg.duration}</strong>
                  </div>
                  <div className="compare-card-row">
                    <span>Destinations</span>
                    <strong>{pkg.destinations.length}</strong>
                  </div>
                  <div className="compare-card-row">
                    <span>Flight</span>
                    <strong>{pkg.flightIncluded ? "✓ Included" : "Not included"}</strong>
                  </div>
                  <div className="compare-card-row">
                    <span>Type</span>
                    <strong>{pkg.packageType}</strong>
                  </div>
                  {discount && (
                    <div className="compare-card-row">
                      <span>Discount</span>
                      <strong style={{ color: "#2d7a3a" }}>Save {discount}%</strong>
                    </div>
                  )}
                  <div className="compare-card-price">₹{formatPrice(pkg.discounted_price)}</div>
                  <button className="book-btn" style={{ marginTop: 10, width: "100%", justifyContent: "center" }}>
                    Book Now <Icon.Arrow />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const DEFAULT_FILTERS = {
  theme: "All",
  duration: "All",
  price: "All",
  packageType: "All",
  flight: "all",
  sort: "Featured",
  search: "",
};

export default function TourPackages() {
  usePageMeta(
  "Travel Offers & Tour Package Deals – MakeBeeTrip",
  "Explore the latest travel offers on flights, hotels, holidays and tour packages. Filter deals by theme, duration and budget, and save on your next trip.",
  "travel offers, flight offers, hotel deals, holiday deals, tour package offers, discount travel deals, last minute deals, travel discounts"
);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"
  const [compareList, setCompareList] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);

  const filtered = useMemo(() => {
    let result = filterAndSort(packages, filters);
    if (filters.flight === "yes") result = result.filter((p) => p.flightIncluded);
    if (filters.flight === "no") result = result.filter((p) => !p.flightIncluded);
    return result;
  }, [filters]);

  const handleToggleCompare = (pkg) => {
    setCompareList((prev) => {
      if (prev.find((p) => p.id === pkg.id)) return prev.filter((p) => p.id !== pkg.id);
      if (prev.length >= 4) return prev; // max 4
      return [...prev, pkg];
    });
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setCompareList([]);
  };

  return (
    <>
      {/* ── Hero ── */}


      {/* ── Search Bar ── */}
      <div className="search-bar-wrap">
        <div className="search-bar">
          <div className="search-input-wrap">
            <span className="search-icon"><Icon.Search /></span>
            <input
              className="search-input"
              placeholder="Search destinations, experiences…"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
          </div>
          {/* <div className="search-divider" /> */}
          {/* <div className="result-count">
            <strong>{filtered.length}</strong> packages found
          </div> */}
        </div>
      </div>

      {/* ── Body: Sidebar + Grid ── */}
      <div className="page-body">
        <Sidebar filters={filters} setFilters={setFilters} onReset={handleReset} />

        <main className="main-content">
          {/* Toolbar */}
          <div className="toolbar">
            <div className="toolbar-left">
              <span className="results-label">{filtered.length} results</span>
              <div className="view-toggle">
                <button
                  className={`view-btn${viewMode === "grid" ? " active" : ""}`}
                  onClick={() => setViewMode("grid")}
                  title="Grid view"
                >
                  <Icon.Grid />
                </button>
                <button
                  className={`view-btn${viewMode === "list" ? " active" : ""}`}
                  onClick={() => setViewMode("list")}
                  title="List view"
                >
                  <Icon.List />
                </button>
              </div>
            </div>
            <select
              className="sort-select"
              value={filters.sort}
              onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))}
            >
              {sortOptions.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>

          {/* Cards */}
          {filtered.length === 0 ? (
            <div className="packages-grid">
              <div className="empty-state">
                <div className="empty-icon">🌍</div>
                <h3 className="empty-title">No packages found</h3>
                <p className="empty-text">
                  We couldn't find packages matching your filters. Try broadening your search or resetting the filters.
                </p>
                <button className="empty-reset-btn" onClick={handleReset}>Reset Filters</button>
              </div>
            </div>
          ) : (
            <div className={`packages-grid${viewMode === "list" ? " list-view" : ""}`}>
              {filtered.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  pkg={pkg}
                  isCompared={!!compareList.find((p) => p.id === pkg.id)}
                  onToggleCompare={handleToggleCompare}
                  isListView={viewMode === "list"}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* ── Compare Bar ── */}
      <div className={`compare-bar${compareList.length > 0 ? " visible" : ""}`}>
        <div className="compare-bar-left">
          <span className="compare-bar-label">Comparing {compareList.length} of 4 packages</span>
          <div className="compare-items">
            {compareList.map((pkg) => (
              <div key={pkg.id} className="compare-item">
                {pkg.title}
                <button
                  className="compare-item-remove"
                  onClick={() => handleToggleCompare(pkg)}
                >×</button>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="compare-clear-btn" onClick={() => setCompareList([])}>Clear</button>
          <button
            className="compare-cta-btn"
            onClick={() => setShowCompareModal(true)}
            disabled={compareList.length < 2}
          >
            Compare Now →
          </button>
        </div>
      </div>

      {/* ── Compare Modal ── */}
      {showCompareModal && (
        <CompareModal items={compareList} onClose={() => setShowCompareModal(false)} />
      )}

    </>
  );
}